import type { MergedOfferItemData, VerifiedFlightSegment, UnverifiedFlight, AirportData } from '@interfaces/offer';
import type { FlightSegment } from '@/interfaces/checkout/offer-data';
import { objectToQuery } from '@services/transform';
import { currencyCodes } from '@/js/utils/priceUtils';
import type { OfferListTypoHotelData } from '@interfaces/offer-list-types/offer-list-typo-hotel-data';
import { sanitizeBookingUrl } from '@utils/urlUtils';

interface CheckoutUrlParams {
	cyid: number;
	rid: number;
	categoryOffer: number;
	amount?: number;
	currency?: string;
	exchangeRateAmount?: number;
	exchangeRateCurrency?: string;
}

/**
 * Extract flight segment data with proper null handling
 */
export const getFlightSegments = (segments?: VerifiedFlightSegment[]) => {
	if (!segments?.length) return { first: null, last: null };
	return {
		first: segments[0],
		last: segments[segments.length - 1],
	};
};

/**
 * Check if a flight has unverified data
 */
export const getUnverifiedFlight = (flight: UnverifiedFlight | Record<string, unknown> | undefined | null): UnverifiedFlight | null => {
	return flight && Object.keys(flight).length ? (flight as UnverifiedFlight) : null;
};

/**
 * Assigns a property value from source to target if target's property is undefined
 */
export const assignIfUndefined = <T extends keyof Partial<AirportData>>(target: AirportData, source: AirportData, property: T): void => {
	if (!target[property] && source[property]) {
		target[property] = source[property];
	}
};

/**
 * Updates flight segments with unverified flight data when needed
 */
export const updateFlightSegments = (
	flightSegments: VerifiedFlightSegment[] | undefined,
	unverifiedFlight: UnverifiedFlight | null
): FlightSegment[] => {
	if (!flightSegments?.length || !unverifiedFlight) return [];

	const firstFlight = { ...flightSegments[0].Departure };
	const lastFlight = { ...flightSegments[flightSegments.length - 1].Arrival };

	assignIfUndefined(firstFlight, unverifiedFlight.Departure, 'Airport');
	assignIfUndefined(firstFlight, unverifiedFlight.Departure, 'AirportCode');
	assignIfUndefined(lastFlight, unverifiedFlight.Arrival, 'Airport');
	assignIfUndefined(lastFlight, unverifiedFlight.Arrival, 'AirportCode');

	if (flightSegments.length === 1) {
		return [
			{
				...flightSegments[0],
				Departure: firstFlight,
				Arrival: lastFlight,
			},
		];
	}

	return [
		{
			...flightSegments[0],
			Departure: firstFlight,
		},
		...flightSegments.slice(1, -1),
		{
			...flightSegments[flightSegments.length - 1],
			Arrival: lastFlight,
		},
	];
};

/**
 * Checks if there are missing airport data in the flight segments
 */
type FlightSegments = {
	firstOutbound: VerifiedFlightSegment | null;
	lastOutbound: VerifiedFlightSegment | null;
	firstInbound: VerifiedFlightSegment | null;
	lastInbound: VerifiedFlightSegment | null;
};

export const checkMissingAirportData = ({ firstOutbound, lastOutbound, firstInbound, lastInbound }: FlightSegments) => {
	return {
		outbound:
			!firstOutbound?.Departure?.Airport ||
			!firstOutbound?.Departure?.AirportCode ||
			!lastInbound?.Arrival?.Airport ||
			!lastInbound?.Arrival?.AirportCode,

		inbound:
			!firstInbound?.Departure?.Airport ||
			!firstInbound?.Departure?.AirportCode ||
			!lastOutbound?.Arrival?.Airport ||
			!lastOutbound?.Arrival?.AirportCode,
	};
};

/**
 * Creates airport parameters string for URL
 */
export const createAirportParams = (flight: UnverifiedFlight): string => {
	return [flight.Departure.Airport, flight.Departure.AirportCode, flight.Arrival.Airport, flight.Arrival.AirportCode].join(';');
};

/**
 * Generates the IBE checkout URL with all necessary parameters
 */
export const generateCheckoutUrl = (
	offerData: MergedOfferItemData,
	hotelData: OfferListTypoHotelData,
	flightSegments: FlightSegments,
	unverifiedFlights: {
		outbound: UnverifiedFlight | null;
		inbound: UnverifiedFlight | null;
	}
): string => {
	const additionalParams: CheckoutUrlParams = {
		cyid: hotelData.Location.City.TtCyid,
		rid: hotelData.Location.Region.TtRid,
		categoryOffer: offerData.Category,
	};
	let bookingUrl = offerData.BookingURL;

	// Handle undefined or check unable status
	if (offerData.Status === 'Undefined' || offerData.Status === 'CheckUnable') {
		additionalParams.amount = offerData.OriginalPrice;
		additionalParams.currency = offerData.OriginalCurrencyCode;

		if (offerData.unverifiedCurrency === currencyCodes.switzerland) {
			additionalParams.exchangeRateAmount = offerData.unverifiedPrice / offerData.OriginalPrice;
			additionalParams.exchangeRateCurrency = offerData.unverifiedCurrency;
		}
	}

	const urlFromString = new URL(sanitizeBookingUrl(bookingUrl));
	const urlSearchParams = new URLSearchParams(urlFromString.searchParams);

	// Check for missing airport data
	const missingData = checkMissingAirportData(flightSegments);
	// Handle outbound flight with missing data
	if (missingData.outbound && unverifiedFlights.outbound) {
		const outboundParams = createAirportParams(unverifiedFlights.outbound);
		urlSearchParams.set('outbound', encodeURIComponent(outboundParams));
		urlSearchParams.set('unverifiedAirport', '1');
		bookingUrl = `${urlFromString.origin}/book?${urlSearchParams.toString()}`;
	}

	// Handle inbound flight with missing data
	if (missingData.inbound && unverifiedFlights.inbound) {
		const inboundParams = createAirportParams(unverifiedFlights.inbound);
		urlSearchParams.set('inbound', encodeURIComponent(inboundParams));
		urlSearchParams.set('unverifiedAirport', '1');
		bookingUrl = `${urlFromString.origin}/book?${urlSearchParams.toString()}`;
	}

	return `${bookingUrl}&${objectToQuery(additionalParams)}`;
};
